import { AppDispatch } from '@/app/store';
import { Location, NavigateFunction, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { PhoneBindingDialog } from '@/components/common/phone/PhoneBindingDialog';
import { ROUTE_HELP_FAQ, ROUTE_MAIN } from '@/app/routes';
import { Theme } from '@mui/material/styles';
import { UserDTO } from '@/types/user/UserDTO';
import {
  dismissPhoneBindingReminder,
  getAuthenticated,
  getCurrentUser,
  getPhoneBindingEnforced,
  getPhoneBindingFlowOwner,
  getPhoneBindingReminderDismissed,
  setPhoneBindingEnforced,
  setPhoneBindingFlowOwner,
  setUser,
  signOut,
} from '@/services/authSlice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetCurrentUserQuery } from '@/services/api/authApiSlice';
import { useMediaQuery } from '@mui/material';
import React, { FC, ReactElement, useEffect } from 'react';
import type { PhoneBindingMode } from '@/types/user/PhoneBindingMode';

interface Props {
  /**
   * The one mode this mount answers for.
   * Each mode is mounted exactly once, at the reach it needs.
   *
   * `REQUIRED` is mounted above the whole route tree, because a user who has yet to bind a number
   * has every request refused and any page left ungated becomes a way around the barrier.
   * `RECOMMENDED` is mounted inside the personal cabinet alone: on the pages a guest may also use,
   * such a user is part-way through paying for a card or a letter, and the reminder has no business interrupting that.
   */
  handles: Exclude<PhoneBindingMode, 'NONE'>;
}

/**
 * Decides whether the signed-in user has to see a phone-binding dialog, and which one.
 */
export const PhoneBindingGate: FC<Props> = (props: Props): ReactElement | null => {
  const { handles } = props;

  const dispatch: AppDispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();
  const location: Location = useLocation();
  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm'));

  const isAuthenticated: boolean = useAppSelector(getAuthenticated);
  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);
  const isReminderDismissed: boolean = useAppSelector(getPhoneBindingReminderDismissed);
  const isEnforced: boolean = useAppSelector(getPhoneBindingEnforced);
  const flowOwner: Exclude<PhoneBindingMode, 'NONE'> | undefined = useAppSelector(getPhoneBindingFlowOwner);

  const isFlowRunning: boolean = flowOwner === handles;

  useEffect((): (() => void) | undefined => {
    if (!isFlowRunning) {
      return undefined;
    }
    return (): void => {
      dispatch(setPhoneBindingFlowOwner(undefined));
    };
  }, [isFlowRunning, dispatch]);

  const isPrerender: boolean = navigator.userAgent === 'ReactSnap' || navigator.userAgent === 'Prerender';

  // The one section the barrier stands down on: the FAQ.
  const isFaqSection: boolean = matchPath(ROUTE_HELP_FAQ, location.pathname) !== null;

  const isExempt: boolean = isFaqSection && !isFlowRunning;

  const { data: currentUser } = useGetCurrentUserQuery(authUser?.id, { skip: !authUser || isExempt });

  // A request the server has already refused outranks the mode the client last read.
  // That mode goes stale whenever enforcement begins or the feature is switched on mid-session,
  // and nothing prompts a re-read until the next mount, so the refusal is the only notice the client gets.
  const mode: PhoneBindingMode | undefined = isEnforced
    ? 'REQUIRED'
    : (currentUser?.phoneBindingMode ?? authUser?.phoneBindingMode);

  const handleDismiss = (): void => {
    dispatch(dismissPhoneBindingReminder());
  };

  const handleSignOut = (): void => {
    navigate(ROUTE_MAIN);
    dispatch(signOut());
  };

  const handleFlowChange = (isRunning: boolean): void => {
    dispatch(setPhoneBindingFlowOwner(isRunning ? handles : undefined));
  };

  const handleBound = (): void => {
    if (!authUser) {
      return;
    }
    dispatch(setPhoneBindingEnforced(false));
    dispatch(setUser({ ...authUser, ...currentUser, phoneBindingMode: 'NONE', token: authUser.token }));
  };

  if (isPrerender) {
    return null;
  }
  if (!isAuthenticated) {
    return null;
  }
  if (!authUser) {
    return null;
  }
  // Everything below decides whether to open a dialog, so all of it is conditional on none being open here.
  if (!isFlowRunning) {
    if (flowOwner) {
      return null;
    }
    if (isFaqSection) {
      return null;
    }
    if (!mode) {
      return null;
    }
    // A mount answers for one mode only, so this also covers 'NONE' — no mount ever handles it.
    if (mode !== handles) {
      return null;
    }
    if (handles === 'RECOMMENDED' && isReminderDismissed) {
      return null;
    }
  }

  const noticeMode: Exclude<PhoneBindingMode, 'NONE'> = mode === 'REQUIRED' ? 'REQUIRED' : 'RECOMMENDED';

  return (
    <PhoneBindingDialog
      open={true}
      mode={noticeMode}
      username={authUser.username}
      isMobile={isMobile}
      onFlowChange={handleFlowChange}
      onBound={handleBound}
      onClose={noticeMode === 'REQUIRED' ? handleSignOut : handleDismiss}
    />
  );
};
